import { Link, useNavigate } from "react-router";
import { ArrowRight, Loader2, Lock, Mail } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { AuthLayout } from "@/components/common/auth-layout";
import { IconInput, PasswordToggle } from "@/components/common/icon-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { firebaseErrorMessage } from "@/lib/firebase";
import { usePageMeta } from "@/lib/page-meta";

export function LoginRoute() {
  usePageMeta({
    title: "Login — HealNova Doctor Portal",
    description: "Login to your HealNova clinician account to manage appointments and profile.",
  });
  return <LoginPage />;
}

function LoginPage() {
  const { signIn, user, loading, configured } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      toast.success("Welcome back to HealNova.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(firebaseErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Doctor portal"
      title="Welcome back"
      subtitle="Login to access your HealNova clinician workspace."
      footer={
        <>
          New to HealNova?{" "}
          <Link to="/signup" className="story-link font-semibold text-primary">
            Create an account
          </Link>
        </>
      }
    >
      {!loading && !configured && (
        <Alert variant="destructive" className="mb-7">
          <AlertDescription>
            Authentication isn't configured yet. Add your Firebase web keys to enable sign-in.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <div className="space-y-2.5">
          <Label htmlFor="email">Email address</Label>
          <IconInput
            icon={Mail}
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="doctor@clinic.com"
          />
        </div>

        <div className="space-y-2.5">
          <div className="flex items-baseline justify-between gap-4">
            <Label htmlFor="password">Password</Label>
            {/* Recovery is handled by the account team until self-serve reset ships. */}
            <a
              href="mailto:support@healnova.ai?subject=Password%20reset"
              className="story-link text-sm font-medium text-primary"
            >
              Forgot password?
            </a>
          </div>
          <IconInput
            icon={Lock}
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            trailing={
              <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
            }
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          className="group h-14 w-full rounded-xl text-base"
          disabled={submitting || loading || !configured}
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          Login to your account
          <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>
      </form>
    </AuthLayout>
  );
}
